import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  PauseIcon,
  PlayIcon,
  XCircleIcon,
  HashtagIcon,
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  GlobeAltIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { marketplaceApi, MessageTemplate } from '../../api/marketplace';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalConfig } from '../../contexts/GlobalConfigContext';
import { getFacilityId } from '../../utils/facilityUtils';
import toast from 'react-hot-toast';
import TemplateForm from './TemplateForm';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

const PLATFORMS = [
  { id: 'whatsapp', name: 'WhatsApp', icon: '💬' },
  { id: 'facebook', name: 'Facebook', icon: '📘' },
  { id: 'instagram', name: 'Instagram', icon: '📷' },
  { id: 'telegram', name: 'Telegram', icon: '📡' },
];

const CATEGORIES = [
  'Bird Sale',
  'Breeding',
  'Health',
  'General',
  'Promotional',
];

export default function MessageTemplatesManager() {
  const { user } = useAuth();
  const { config } = useGlobalConfig();
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<MessageTemplate | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [filters, setFilters] = useState({
    platform: 'all',
    category: 'all',
    search: '',
  });

  // Use the utility function to get a proper facility ID
  const facilityId = getFacilityId(user?.facilityId);

  useEffect(() => {
    loadTemplates();
  }, [filters]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      // Only pass platform if it's not 'all'
      const platform = filters.platform === 'all' ? undefined : filters.platform;
      const data = await marketplaceApi.getMarketingTemplates(facilityId, platform);
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (templateId: string | undefined, formData: any) => {
    try {
      await marketplaceApi.createMarketingTemplate(facilityId, formData);
      toast.success('Template created successfully!');
      setShowForm(false);
      loadTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    }
  };

  const handleUpdateTemplate = async (templateId: string, formData: any) => {
    try {
      if (!templateId) {
        console.error('Template ID is undefined');
        toast.error('Template ID is missing');
        return;
      }
      await marketplaceApi.updateMarketingTemplate(facilityId, templateId, formData);
      toast.success('Template updated successfully!');
      setShowForm(false);
      setIsEditing(false);
      loadTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await marketplaceApi.deleteMarketingTemplate(facilityId, templateId);
      toast.success('Template deleted successfully!');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleToggleActive = async (templateId: string, isActive: boolean) => {
    try {
      await marketplaceApi.updateMarketingTemplate(facilityId, templateId, { isActive });
      toast.success(`Template ${isActive ? 'activated' : 'deactivated'} successfully!`);
      loadTemplates();
    } catch (error) {
      console.error('Error updating template status:', error);
      toast.error('Failed to update template status');
    }
  };



  const getPlatformIcon = (platform: string) => {
    const platformData = PLATFORMS.find(p => p.id === platform);
    return platformData ? platformData.icon : '📄';
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-violet-100 text-violet-800';
  };

  const getStatusIcon = (isActive: boolean) => {
    return isActive ? <ChatBubbleLeftRightIcon className="h-4 w-4" /> : <DevicePhoneMobileIcon className="h-4 w-4" />;
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  const generatePreview = (template: MessageTemplate) => {
    let preview = template.content;
    
    // Replace variables with sample data
    const sampleData = {
      birdName: 'Blue Parrot',
      species: 'African Grey',
      price: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'ZAR', // Assuming config is not available here, use a default or remove if not needed
      }).format(5000),
      age: '2 years',
      gender: 'Male',
      location: 'Centurion, South Africa',
      contactInfo: 'Contact us at +27 12 345 6789',
    };

    Object.entries(sampleData).forEach(([key, value]) => {
      preview = preview.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    });

    return preview;
  };

  const filteredTemplates = templates.filter(template => {
    const matchesPlatform = filters.platform === 'all' || template.platform === filters.platform;
    const matchesCategory = filters.category === 'all' || (template.categories || []).includes(filters.category);
    const matchesSearch = (template.name || template.templateName || '').toLowerCase().includes(filters.search?.toLowerCase() || '') ||
                          (template.content || '').toLowerCase().includes(filters.search?.toLowerCase() || '');

    return matchesPlatform && matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Message Templates</h2>
          <p className="mt-1 text-sm text-slate-500">
            Create and manage message templates for different platforms
          </p>
        </div>
        <button
          onClick={() => {
            setIsEditing(false);
            setShowForm(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Template
        </button>
      </div>

      {/* Filters */}
      <div className="flex space-x-4">
        <div className="flex space-x-2">
          <button
            onClick={() => setFilters(prev => ({ ...prev, platform: 'all' }))}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              filters.platform === 'all'
                ? 'bg-blue-100 text-cyan-700'
                : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
            }`}
          >
            All Platforms
          </button>
          <select
            value={filters.platform}
            onChange={(e) => setFilters(prev => ({ ...prev, platform: e.target.value }))}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            {PLATFORMS.map((platform) => (
              <option key={platform.id} value={platform.id}>
                {platform.icon} {platform.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={() => setFilters(prev => ({ ...prev, category: 'all' }))}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              filters.category === 'all'
                ? 'bg-blue-100 text-cyan-700'
                : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
            }`}
          >
            All Categories
          </button>
          <select
            value={filters.category}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            {CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1">
          <input
            type="text"
            placeholder="Search templates..."
            value={filters.search || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{getPlatformIcon(template.platform)}</span>
                                     <h4 className="text-lg font-medium text-slate-900">{template.name || template.templateName}</h4>
                </div>
                <div className="flex items-center space-x-2">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(template.isActive !== false)}`}>
                  {getStatusIcon(template.isActive !== false)}
                  <span className="ml-1">{template.isActive !== false ? 'Active' : 'Inactive'}</span>
                </span>
                {template.isDefault && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Default
                  </span>
                )}
                </div>
              </div>

              <p className="text-sm text-slate-600 mb-4 line-clamp-3">{template.content}</p>

              <div className="flex items-center space-x-2 mb-4">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                  {template.platform}
                </span>
                {(template.categories || []).slice(0, 2).map((category) => (
                  <span key={category} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                    {category}
                  </span>
                ))}
                {(template.categories || []).length > 2 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                    +{(template.categories || []).length - 2}
                  </span>
                )}
              </div>

              {template.hashtags.length > 0 && (
                <div className="flex items-center space-x-1 mb-4">
                  <GlobeAltIcon className="h-4 w-4 text-slate-400" />
                  <div className="flex flex-wrap gap-1">
                    {template.hashtags.slice(0, 3).map((hashtag) => (
                      <span key={hashtag} className="text-xs text-slate-500">
                        #{hashtag}
                      </span>
                    ))}
                    {template.hashtags.length > 3 && (
                      <span className="text-xs text-slate-500">
                        +{template.hashtags.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="text-sm text-slate-500 mb-4">
                Used {template.usage?.totalUses || 0} times
                {template.usage?.lastUsedAt && (
                  <span> • Last used: {formatDate(template.usage.lastUsedAt)}</span>
                )}
                {template.usage?.successRate && template.usage.successRate > 0 && (
                  <span> • Success: {template.usage.successRate.toFixed(1)}%</span>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setSelectedTemplate(template);
                    setShowForm(false); // Don't show form modal
                    setIsEditing(false);
                  }}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Preview
                </button>
                <button
                  onClick={() => {
                    setSelectedTemplate(template);
                    setShowForm(true);
                    setIsEditing(true);
                  }}
                  className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleToggleActive(template.id, !(template.isActive !== false))}
                  className={`inline-flex items-center justify-center px-3 py-2 border shadow-sm text-sm font-medium rounded-md ${
                    template.isActive !== false
                      ? 'border-red-300 text-red-700 bg-white hover:bg-red-50'
                      : 'border-green-300 text-green-700 bg-white hover:bg-green-50'
                  }`}
                >
                  {template.isActive !== false ? <DevicePhoneMobileIcon className="h-4 w-4" /> : <PaperAirplaneIcon className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => handleDeleteTemplate(template.id)}
                  className="inline-flex items-center justify-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-slate-400" />
          <h3 className="mt-2 text-sm font-medium text-slate-900">No templates found</h3>
          <p className="mt-1 text-sm text-slate-500">
            Get started by creating your first message template.
          </p>
        </div>
      )}

      {/* Create/Edit Template Modal */}
      <BrandModal
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setSelectedTemplate(null);
        }}
        maxWidthClass="max-w-4xl"
      >
        <BrandModalHeader
          title={isEditing ? 'Edit Template' : 'Create New Template'}
          onClose={() => {
            setShowForm(false);
            setSelectedTemplate(null);
          }}
        />
        <BrandModalBody>
          <TemplateForm
            template={selectedTemplate}
            onSubmit={isEditing ? handleUpdateTemplate : handleCreateTemplate}
            onCancel={() => {
              setShowForm(false);
              setSelectedTemplate(null);
            }}
            platforms={PLATFORMS}
            categories={CATEGORIES}
          />
        </BrandModalBody>
      </BrandModal>

      {/* Preview Modal */}
      <BrandModal
        open={!!selectedTemplate && !showForm}
        onClose={() => setSelectedTemplate(null)}
        maxWidthClass="max-w-2xl"
      >
        {selectedTemplate ? (
          <>
            <BrandModalHeader
              title="Template Preview"
              onClose={() => setSelectedTemplate(null)}
            />
            <BrandModalBody>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">{selectedTemplate.name || selectedTemplate.templateName}</h4>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">{getPlatformIcon(selectedTemplate.platform)}</span>
                    <span className="text-sm text-slate-600">{selectedTemplate.platform}</span>
                  </div>
                </div>

                <div className="bg-slate-50/80 p-4 rounded-lg">
                  <h5 className="font-medium text-slate-900 mb-2">Preview:</h5>
                  <div className="whitespace-pre-wrap text-sm text-slate-700">
                    {generatePreview(selectedTemplate)}
                  </div>
                </div>

                {selectedTemplate.hashtags.length > 0 && (
                  <div>
                    <h5 className="font-medium text-slate-900 mb-2">Hashtags:</h5>
                    <div className="flex flex-wrap gap-1">
                      {selectedTemplate.hashtags.map((hashtag) => (
                        <span key={hashtag} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                          #{hashtag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedTemplate.mediaTemplate && (
                  <div>
                    <h5 className="font-medium text-slate-900 mb-2">Media Settings:</h5>
                    <div className="text-sm text-slate-600">
                      {selectedTemplate.mediaTemplate.includeImages && (
                        <div>• Include images ({selectedTemplate.mediaTemplate.imageCount} max)</div>
                      )}
                      {selectedTemplate.mediaTemplate.includeVideo && <div>• Include video</div>}
                      {selectedTemplate.mediaTemplate.includeDocuments && <div>• Include documents</div>}
                    </div>
                  </div>
                )}
              </div>
            </BrandModalBody>
            <BrandModalFooter>
              <button
                type="button"
                onClick={() => setSelectedTemplate(null)}
                className="btn btn-secondary text-sm"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(true);
                  setIsEditing(true);
                }}
                className="btn btn-primary text-sm"
              >
                <PencilIcon className="h-4 w-4 mr-1 inline" />
                Edit Template
              </button>
            </BrandModalFooter>
          </>
        ) : null}
      </BrandModal>
    </div>
  );
}
